import React, { useState, useCallback, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { initialValues, validationSchema } from "../../../schema/user_roles/userRolesSchema";

import { useUserRoles, roleTypes, targetTypes } from "../../../hooks/api/useUserRoles";
import { useUsers, userTypes, targetTypes as userTargetTypes } from "../../../hooks/api/useUsers";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewUserRole({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@role"] })

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useUserRoles({ roleType: roleTypes.role, targetType: targetTypes.role });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.userRole.id, scopeTypes.userRole.role.id)
    const { list: listUsers } = useUsers({ userType: '', targetType: '' });

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [dataUsers, setDataUsers] = useState([])

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                role: {
                    role_name: currentValues?.roleTitle,
                    role_type: currentValues?.userRouteType,
                    description: currentValues?.description,
                    status: currentValues?.status ? 1 : 0,
                },
                users: currentValues?.userId
            }

            const response = await create({ data, scopeType: "", targetType: targetTypes.role });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/userRoles"))

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const fetchUsers = async ({ routeType }) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "username",
                    dir: 0
                }
            }

            if (routeType === routeTypes.admin) {

                props.userType = userTypes.admin
            }
            if (routeType === routeTypes.retail) {

                props.userType = userTargetTypes.retail
                props.dataTypes = ["branch_user_count"]

            }
            if (routeType === routeTypes.customer) {

                props.userType = userTargetTypes.customer
                props.dataTypes = ["branch_user_count"]

            }
            if (routeType === routeTypes.workstation) {

                props.userType = userTargetTypes.worker
                props.dataTypes = ["location_count"]

            }

            const result = await listUsers(props)

            if (routeType === routeTypes.admin) {
                setDataUsers(result?.data?.administrators)
            }
            if (routeType === routeTypes.retail) {

                setDataUsers(result?.data?.retailers)
            }
            if (routeType === routeTypes.customer) {
                setDataUsers(result?.data?.customers)
            }
            if (routeType === routeTypes.workstation) {

                setDataUsers(result?.data?.workers)
            }

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        return true

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;


    const RenderUserOptionItemDefault = (props) => {

        const {
            item
        } = props

        return (
            <>
                <ListItemText
                    primary={
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: '600'
                            }}
                        >
                            {`${item?.firstname} ${item?.lastname}`} ({`${item?.username}`})
                        </Typography>
                    }
                />
            </>
        )
    }

    const RenderUserOptionItemRetail = (props) => {

        const {
            item,
            viewType = 'main'
        } = props

        let secondary = ''

        if (viewType === "main") {
            secondary = `${item?.company_name} (${item?.branch_user_count} Users)`
        } else { // for branch
            // check the parent name
            let parentUserId = item?.parent_user_id

            const check = dataUsers.filter(i => i.user_id === parentUserId)

            secondary = check.length ? check[0].company_name : "-"

        }

        return (
            <>
                <ListItemText
                    primary={
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: '600'
                            }}
                        >
                            {`${item?.firstname} ${item?.lastname}`} ({`${item?.username}`})
                        </Typography>
                    }
                    secondary={
                        <Typography
                            sx={{
                                fontSize: 13,
                                // fontWeight: '600'
                            }}
                        >
                            {secondary}
                        </Typography>
                    }
                />
            </>
        )
    }

    const RenderUserOptionItemCustomer = (props) => {

        const {
            item,
            viewType = 'main'
        } = props

        let secondary = ''

        if (viewType === "main") {
            secondary = `${item?.company_name} (${item?.branch_user_count} Users)`
        } else { // for branch
            // check the parent name
            let parentUserId = item?.parent_user_id

            const check = dataUsers.filter(i => i.user_id === parentUserId)

            secondary = check.length ? check[0].company_name : "-"

        }

        return (
            <>
                <ListItemText
                    primary={
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: '600'
                            }}
                        >
                            {`${item?.firstname} ${item?.lastname}`} ({`${item?.username}`})
                        </Typography>
                    }
                    secondary={
                        <Typography
                            sx={{
                                fontSize: 13,
                                // fontWeight: '600'
                            }}
                        >
                            {secondary}
                        </Typography>
                    }
                />
            </>
        )
    }

    const RenderUserOptionItemWorkstation = (props) => {

        const {
            item
        } = props

        return (
            <>
                <ListItemText
                    primary={
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: '600'
                            }}
                        >
                            {`${item?.firstname} ${item?.lastname}`} ({`${item?.username}`})
                        </Typography>
                    }
                    secondary={
                        <Typography
                            sx={{
                                fontSize: 13,
                                // fontWeight: '600'
                            }}
                        >
                            {`${item?.location_count} Locations`}
                        </Typography>
                    }
                />
            </>
        )
    }

    const RenderUserOptionItem = (props) => {

        // for retail
        if (values.userRouteType === routeTypes.retail) {
            return (
                <RenderUserOptionItemRetail
                    {...props}
                />
            )
        }

        // for customer
        if (values.userRouteType === routeTypes.customer) {
            return (
                <RenderUserOptionItemCustomer
                    {...props}
                />
            )
        }

        // for worker
        if (values.userRouteType === routeTypes.workstation) {
            return (
                <RenderUserOptionItemWorkstation
                    {...props}
                />
            )
        }

        // for normal user
        return (
            <RenderUserOptionItemDefault
                {...props}
            />
        )

    }


    const RenderRolesInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('rolesAssignation')}
                        subTitle={t('addingRoleTitleWithDetails')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>

                        <Grid item xs={12} md={6} textAlign="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="status"
                                        checked={Boolean(values.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.status) ? t("enabled") : t("disabled")
                                }
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.userRouteType && Boolean(errors.userRouteType)}
                            >
                                <InputLabel id="userRouteType">{t('userRouteType')}</InputLabel>
                                <Select
                                    labelId="userRouteType"
                                    label={t('userRouteType')}
                                    name={"userRouteType"}
                                    onChange={(event) => {
                                        setFieldValue(`userRouteType`, event.target.value)
                                        fetchUsers({ routeType: event.target.value })
                                    }}
                                    value={values.userRouteType}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >
                                    {
                                        Object.keys(routeTypes).map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i}>{i}</MenuItem>
                                            )
                                        })
                                    }
                                </Select>
                                <FormHelperText>
                                    {touched.userRouteType && errors.userRouteType}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={12}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"roleTitle"}
                                label={t('roleTitle')}
                                type="text"
                                variant="outlined"
                                value={values.roleTitle}
                                onChange={handleChange}
                                error={touched.roleTitle && Boolean(errors.roleTitle)}
                                helperText={touched.roleTitle && errors.roleTitle}
                            />
                        </Grid>


                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.userId && Boolean(errors.userId)}
                            >
                                <InputLabel id="userId">{t('mainUsers')}</InputLabel>
                                <Select
                                    fullWidth
                                    labelId="userId"
                                    label={t('mainUsers')}
                                    name={"userId"}
                                    // onChange={handleChange}
                                    onChange={(event) => {

                                        let val = event.target.value;
                                        setFieldValue('userId', val)

                                    }}
                                    value={values.userId}
                                    renderValue={(selected) => {

                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {
                                                    selected.map((value, index2) => {

                                                        // get back the username based on the id
                                                        const user = dataUsers.filter(i => i.user_id === value)

                                                        const userName = user.length ? user[0].username : '-'

                                                        return (
                                                            <Chip key={index2} label={userName} />
                                                        )
                                                    })

                                                }
                                            </Box>
                                        )

                                    }}
                                    multiple
                                    input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                                >
                                    {
                                        dataUsers.map((i, index) => {
                                            return (
                                                <MenuItem
                                                    key={index}
                                                    value={i?.user_id}
                                                    divider
                                                >
                                                    <RenderUserOptionItem
                                                        item={i}
                                                    />
                                                </MenuItem>
                                            )
                                        })
                                    }
                                </Select>
                                <FormHelperText>
                                    {touched.userId && errors.userId}
                                </FormHelperText>

                                {/* {JSON.stringify(values.userId)} */}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={12}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                multiline
                                rows={3}
                                name={"description"}
                                label={t('description')}
                                type="text"
                                variant="outlined"
                                value={values.description}
                                onChange={handleChange}
                                error={touched.description && Boolean(errors.description)}
                                helperText={touched.description && errors.description}
                            />
                        </Grid>

                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderForm = () => {

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>

                        {
                            RenderRolesInfo()
                        }

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newRole })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("newRole")}
                breadcrumbs={[
                    {
                        displayText: t('roles'),
                        props: {
                            href: getRoute(`settings/userRoles`)
                        }
                    },
                    {
                        displayText: t("newRole"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}